import { type ChangeEvent, type FormEvent, useEffect, useState } from 'react'
import { api, type CalendarFund, type CureSettings, type Holiday, type SettlementCalendar } from '../api'
import { RowMenu } from '../components/RowMenu'
import { SideNav } from '../components/SideNav'
import { DeskCell, DeskHeader, useDesks } from '../desks'
import { sortRows, useSort } from '../components/Grid'

/**
 * Settlement calendars and escalation — TECHNICAL_DESIGN.md §6.5. The Snapshot Evaluator pack.
 *
 * A cure obligation's deadline is the last settlement day on or before the end of the
 * fund's sell-by period, so the calendar is what the deadline is counted on: a weekend,
 * fixed when the calendar is made, and its holidays. **Holidays arrive as a list** —
 * pasted, or read from the file a settlement provider publishes — and a list with a
 * line that does not read is refused whole, every line named, so it is corrected once.
 *
 * Three parts, listed down the left as Settings lists its screens, in the order the
 * screen is used: **Funds** first, since choosing a fund's calendar is the everyday
 * change; **Calendars**, with their holidays; **Escalation**, set once. Each part is laid
 * out the same way, a heading, a line saying what it is, then its content, and none is
 * framed, so moving between them changes the content and nothing else.
 */

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

type View = 'funds' | 'calendars' | 'escalation'

const VIEWS: [View, string, string][] = [
  ['funds', 'Funds', 'The calendar each fund’s deadlines fall on'],
  ['calendars', 'Calendars', 'Weekends and holidays, and the firm’s default'],
  ['escalation', 'Escalation', 'How many days before a deadline obligations escalate'],
]

function said(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

function weekend(days: number[]): string {
  return days.map((d) => DAYS[d - 1] ?? String(d)).join(', ') || 'None'
}

export function SettlementCalendars() {
  const [view, setView] = useState<View>('funds')
  const [reads, setReads] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [settings, setSettings] = useState<CureSettings | null>(null)
  const [days, setDays] = useState('')
  const [calendars, setCalendars] = useState<SettlementCalendar[] | null>(null)
  const [funds, setFunds] = useState<CalendarFund[]>([])
  /** Desk first, sortable and filterable, for somebody across the whole firm (decision 91). */
  const [desk, setDesk] = useState('')
  const { sort, onSort } = useSort()
  const desks = useDesks()
  const [chosen, setChosen] = useState<string | null>(null)
  const [holidays, setHolidays] = useState<Holiday[]>([])
  const [pasted, setPasted] = useState('')
  const [adding, setAdding] = useState(false)
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [off, setOff] = useState<number[]>([6, 7])
  const [isDefault, setIsDefault] = useState(false)

  useEffect(() => {
    let live = true
    Promise.all([api.cureSettings(), api.settlementCalendars()])
      .then(([s, c]) => {
        if (!live) return
        setSettings(s)
        setDays((current) => current || s.escalate_days.join(', '))
        setCalendars(c.calendars)
        setFunds(c.funds)
      })
      .catch((e) => live && setError(said(e)))
    return () => {
      live = false
    }
  }, [reads])

  useEffect(() => {
    if (!chosen) return
    let live = true
    api
      .holidays(chosen)
      .then((found) => live && setHolidays(found))
      .catch((e) => live && setError(said(e)))
    return () => {
      live = false
    }
  }, [chosen, reads])

  async function act(work: () => Promise<string | null>) {
    setError(null)
    setDone(null)
    try {
      setDone(await work())
      setReads((n) => n + 1)
    } catch (e) {
      setError(said(e))
    }
  }

  function saveEscalation(e: FormEvent) {
    e.preventDefault()
    const numbers = days
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number)
    void act(async () => {
      const saved = await api.setCureEscalation(numbers)
      setDays(saved.escalate_days.join(', '))
      return 'Escalation saved.'
    })
  }

  function create(e: FormEvent) {
    e.preventDefault()
    void act(async () => {
      const made = await api.createSettlementCalendar({ code, name, weekend: off, is_default: isDefault })
      setAdding(false)
      setCode('')
      setName('')
      setOff([6, 7])
      setIsDefault(false)
      setChosen(made.code)
      return `${made.code} added. Add its holidays below.`
    })
  }

  function addHolidays(e: FormEvent) {
    e.preventDefault()
    if (!chosen) return
    void act(async () => {
      const result = await api.addHolidays(chosen, pasted)
      setPasted('')
      return (
        [
          `${result.added} holiday${result.added === 1 ? '' : 's'} added`,
          result.already ? `${result.already} already there` : '',
          result.deadlines_moved ? `${result.deadlines_moved} deadline(s) brought forward` : '',
        ]
          .filter(Boolean)
          .join('; ') + '.'
      )
    })
  }

  async function readFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) setPasted(await file.text())
    e.target.value = ''
  }

  const defaultCode = calendars?.find((c) => c.is_default)?.code

  return (
    <section className="calendars">
      <header className="page-head">
        <h2>Settlement calendars</h2>
        <p className="note">
          The calendars cure obligation deadlines fall on, the calendar each fund uses, and when obligations
          escalate. A deadline is the last settlement day on or before the end of the fund’s sell-by period.
        </p>
      </header>

      <div className="settings-layout">
        <SideNav
          label="Settlement calendars"
          sections={[
            {
              title: 'Deadlines',
              hint: 'What cure obligation deadlines are counted on, and when they escalate',
              entries: VIEWS.map(([id, label, blurb]) => ({
                key: id,
                label,
                blurb,
                showing: view === id,
                select: () => {
                  setView(id)
                  setDone(null)
                  setError(null)
                },
              })),
            },
          ]}
        />
        <div className="settings-content">
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          {done && (
            <p className="done" role="status">
              {done}
            </p>
          )}

          {view === 'funds' && (
            <div className="calendars-part" data-testid="calendars-funds-tab">
              <h3>Funds</h3>
              <p className="note">
                Each fund’s cure obligation deadlines fall on the calendar chosen here, or on the firm’s default.
                Obligations already running keep the calendar they opened on.
                {calendars !== null && calendars.length === 0 && ' Add a calendar under Calendars to choose one here.'}
              </p>
              <div className="table-scroll">
                <table className="grid" data-testid="calendar-funds">
                  <thead>
                    <tr>
                      <DeskHeader sort={sort} onSort={onSort} value={desk} onChange={setDesk} />
                      <th>Code</th>
                      <th>Name</th>
                      <th>Calendar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortRows(
                      funds.filter((f) => !desk || desks.fund(f.id) === desk),
                      sort,
                      { desk: (f) => desks.fund(f.id) ?? '' },
                    ).map((f) => (
                      <tr key={f.id} data-testid="calendar-fund">
                        <DeskCell fund={f.id} />
                        <td>
                          <code>{f.code}</code>
                        </td>
                        <td>{f.name}</td>
                        <td>
                          <select
                            aria-label={`Calendar for ${f.code}`}
                            value={f.calendar_code ?? ''}
                            onChange={(e) =>
                              void act(async () => {
                                const picked = e.target.value || null
                                await api.assignSettlementCalendar(f.id, picked)
                                return `${f.code} now uses ${picked ?? 'the firm’s default'}.`
                              })
                            }
                          >
                            <option value="">
                              {defaultCode ? `The firm’s default (${defaultCode})` : 'Calendar days'}
                            </option>
                            {(calendars ?? []).map((c) => (
                              <option key={c.code} value={c.code}>
                                {c.code} · {c.name}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {view === 'calendars' && (
            <div className="calendars-part" data-testid="calendars-tab">
              <h3>Calendars</h3>
              <p className="note">
                Each calendar’s weekend and holidays. A fund without a calendar of its own uses the firm’s default.
              </p>
              {!adding && (
                <p className="actions">
                  <button type="button" className="ghost" onClick={() => setAdding(true)}>
                    Add a calendar
                  </button>
                </p>
              )}
              {adding && (
                <form className="cure-form" onSubmit={create} data-testid="add-calendar">
                  <h4>Add a calendar</h4>
                  <label>
                    Code
                    <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="TARGET2" />
                  </label>
                  <label>
                    Name
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Euro settlement days" />
                  </label>
                  <fieldset className="weekday-picks">
                    <legend>Weekend</legend>
                    {DAYS.map((label, i) => (
                      <label key={label} className="inline">
                        <input
                          type="checkbox"
                          checked={off.includes(i + 1)}
                          onChange={(e) =>
                            setOff(e.target.checked ? [...off, i + 1].sort() : off.filter((d) => d !== i + 1))
                          }
                        />
                        {label}
                      </label>
                    ))}
                  </fieldset>
                  <label className="inline">
                    <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
                    The firm’s default, for every fund without a calendar of its own
                  </label>
                  <p className="actions">
                    <button type="submit" disabled={!code.trim() || !name.trim()}>
                      Add
                    </button>
                    <button type="button" className="ghost" onClick={() => setAdding(false)}>
                      Cancel
                    </button>
                  </p>
                </form>
              )}

              {calendars === null ? (
                !error && <p className="loading">Loading…</p>
              ) : calendars.length === 0 ? (
                <p className="empty">No calendars yet. Add the calendar your funds settle on, and its holidays.</p>
              ) : (
                <div className="table-scroll">
                  <table className="grid stacked" data-testid="calendar-list">
                    <thead>
                      <tr>
                        <th>Calendar</th>
                        <th>Weekend</th>
                        <th className="num">Holidays</th>
                        <th>Funds on it</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {calendars.map((c) => (
                        <tr key={c.code} data-testid="calendar-row">
                          <td>
                            <strong>{c.code}</strong>
                            {c.is_default && <span className="verdict-count pass">Firm default</span>}
                            <span className="clause">{c.name}</span>
                          </td>
                          <td>{weekend(c.weekend)}</td>
                          <td className="num">
                            {c.holidays}
                            {c.last_holiday && <span className="clause">to {c.last_holiday}</span>}
                          </td>
                          <td>{c.funds.join(', ') || (c.is_default ? 'Every fund without its own' : '—')}</td>
                          <td className="run-actions">
                            <RowMenu
                              label={`Actions for ${c.code}`}
                              testId="calendar-menu"
                              items={[
                                { label: 'Holidays', onSelect: () => setChosen(c.code) },
                                c.is_default
                                  ? {
                                      label: 'No longer the firm’s default',
                                      onSelect: () =>
                                        void act(async () => {
                                          await api.updateSettlementCalendar(c.code, { is_default: false })
                                          return `${c.code} is no longer the firm’s default.`
                                        }),
                                    }
                                  : {
                                      label: 'Make the firm’s default',
                                      onSelect: () =>
                                        void act(async () => {
                                          await api.updateSettlementCalendar(c.code, { is_default: true })
                                          return `${c.code} is the firm’s default.`
                                        }),
                                    },
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {chosen && (
                <div className="calendars-holidays" data-testid="holidays">
                  <h4>Holidays on {chosen}</h4>
                  <form className="cure-form" onSubmit={addHolidays}>
                    <label>
                      Add holidays, one a line
                      <textarea
                        rows={4}
                        value={pasted}
                        onChange={(e) => setPasted(e.target.value)}
                        placeholder={'2026-12-25, Christmas Day\n2026-12-26, Boxing Day'}
                        data-testid="holidays-text"
                      />
                    </label>
                    <label>
                      Or read a file
                      <input type="file" accept=".csv,.txt" onChange={(e) => void readFile(e)} />
                    </label>
                    <p className="actions">
                      <button type="submit" disabled={!pasted.trim()}>
                        Add holidays
                      </button>
                      <button type="button" className="ghost" onClick={() => setChosen(null)}>
                        Close
                      </button>
                    </p>
                  </form>
                  {holidays.length === 0 ? (
                    <p className="empty">No holidays on {chosen} yet.</p>
                  ) : (
                    <div className="table-scroll">
                      <table className="grid" data-testid="holiday-list">
                        <thead>
                          <tr>
                            <th>Day</th>
                            <th>Holiday</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {holidays.map((h) => (
                            <tr key={h.day}>
                              <td>{h.day}</td>
                              <td>{h.name ?? '—'}</td>
                              <td>
                                <button
                                  type="button"
                                  className="link"
                                  onClick={() =>
                                    void act(async () => {
                                      await api.removeHoliday(chosen, h.day)
                                      return `${h.day} removed from ${chosen}.`
                                    })
                                  }
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {view === 'escalation' && (
            <form className="calendars-part cure-form" onSubmit={saveEscalation} data-testid="escalation">
              <h3>Escalation</h3>
              <p className="note">
                Up to three numbers of days before the deadline. Each reached escalates an obligation once more, and
                compliance sees it in My pending actions.
                {settings?.updated_by && ` Last changed by ${settings.updated_by}.`}
              </p>
              <label>
                Days before the deadline
                <input value={days} onChange={(e) => setDays(e.target.value)} placeholder="14, 3" />
              </label>
              <p className="actions">
                <button type="submit">Save</button>
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  )
}
